import { useState } from 'react';
import { motion } from 'framer-motion';
import PartnerAdmin from './PartnerAdmin';

const PartnerNetwork = ({ customPartners = null, showAdmin = false }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [dynamicPartners, setDynamicPartners] = useState([]);

  // Partner data - you can easily modify this to add more partners
  const partners = [
    {
      id: 1,
      name: "NSUT",
      logo: "https://d2lk14jtvqry1q.cloudfront.net/media/large_Netaji_Subhas_University_of_Technology_Delhi_NSUT_Delhi_92808c338f_2844806ac3.png",
      website: "https://nsut.ac.in/en",
      category: "education",
      description: "A premier engineering university in Delhi fostering innovation, research, and excellence in technology and science.",
      established: "1983"
    },
    {
      id: 2,
      name: "Dragonfly Aerospace",
      logo: "https://www.dragonflyaerospace.in/assets/logo.png",
      website: "https://www.dragonflyaerospace.in/",
      category: "aerospace",
      description: "A pioneering aerospace company specializing in satellite imaging, drone systems, and advanced aerospace technologies.",
      established: "2024"
    },
    {
      id: 3,
      name: "NSUT Magnet",
      logo: "https://nsutmagnet.tech/assets/Magnet-BgGiFW75.png",
      website: "https://nsutmagnet.tech/",
      category: "student initiative",
      description: "A student-driven platform by NSUT that simplifies campus life with digital tools for attendance, notes, and event updates.",
      established: "2023"
    }
  ];


  // Use custom partners if provided, otherwise use default + dynamic
  const allPartners = customPartners || [...partners, ...dynamicPartners];

  const categories = [
    { id: 'all', name: 'All Partners', count: allPartners.length },
    { id: 'student initiative', name: 'Technology', count: allPartners.filter(p => p.category === 'student initiative').length },
    { id: 'aerospace', name: 'Research', count: allPartners.filter(p => p.category === 'aerospace').length },

  ];

  const filteredPartners = activeCategory === 'all'
    ? allPartners
    : allPartners.filter(partner => partner.category === activeCategory);

  const handleAddPartner = (newPartner) => {
    setDynamicPartners(prev => [...prev, newPartner]);
  };

  return (
    <section className="py-20 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Our{" "}
            <span className="relative inline-block px-4 py-2 rounded-2xl">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                Partner Network
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 blur-xl opacity-50 -z-10 rounded-2xl"></span>
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Collaborating with industry leaders to push the boundaries of innovation and technology
          </motion.p>
        </div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-full transition-all duration-300 backdrop-blur-md border ${activeCategory === category.id
                ? 'bg-white/10 border-white/30 text-white shadow-lg shadow-blue-500/20'
                : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </motion.div>

        {/* Partners Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredPartners.map((partner, index) => (
            <motion.div
              key={partner.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 group"
            >
              {/* Partner Logo */}
              <div className="mb-6 flex justify-center">
                <div className="w-32 h-16 bg-slate-800 rounded-lg overflow-hidden flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <img
                    src={partner.logo}
                    alt={`${partner.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Partner Info */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">{partner.name}</h3>
                <p className="text-gray-400 text-sm mb-3">{partner.description}</p>
                <div className="flex justify-center items-center gap-4 text-xs text-gray-500">
                  <span className="bg-slate-800 px-2 py-1 rounded-full capitalize">
                    {partner.category}
                  </span>
                  <span>Est. {partner.established}</span>
                </div>
              </div>

              {/* Visit Website Button */}
              <div className="text-center">
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-lg hover:bg-white/20 hover:border-white/30 hover:scale-105 transition-all duration-300 text-sm font-medium shadow-lg"
                >
                  Visit Website
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Admin Panel */}
        {showAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12"
          >
            <PartnerAdmin
              onAddPartner={handleAddPartner}
              partners={allPartners}
            />
          </motion.div>
        )}

        {/* Partnership CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-600/10 border border-blue-500/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Interested in Partnership?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Join our network of innovative partners and collaborate with us to shape the future of technology and aerospace.
            </p>
            <button
              className="px-8 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-lg hover:bg-white/20 hover:border-white/30 hover:scale-105 transition-all duration-300 font-medium shadow-lg"
              onClick={() => window.location.href = "mailto:aeronauts.nsut@gmail.com?subject=Partnership%20Inquiry&body=Hi%20Aeronauts%20Team,%0D%0A%0D%0AI'd%20like%20to%20partner%20with%20you.%20Please%20share%20the%20details."}
            >
              Become a Partner
            </button>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerNetwork;